<template>
	<div>
		<div class="row mb-4">
			<div class="col-9 d-flex align-items-center">
				<h1 class="mb-0">{{ $t("upload") }}</h1>
			</div>
			<div class="col-3">
				<!-- <b-dropdown class="float-right" :text="$t('mode')" variant="primary"
					v-if="$UserSession.hasPriv.call(this, 'TIMETABLE_ADMIN')">
					<b-dropdown-item @click="$router.push('/timetables/settings')">{{ $t("groups") }}</b-dropdown-item>
					<b-dropdown-item @click="$router.push('/timetables/session-types/add')">{{ $t("students") }}</b-dropdown-item>
				</b-dropdown> -->
			</div>
		</div>

		<StepIndicator v-if="step <= 4" :steps="stepLabels" :current-step="step" class="mb-4" />

		<StepPreview
			v-if="step === 1"
			:title="$t('upload-timetable-csv')"
			:show-back="false"
			:loading-preview="loadingPreview"
			:preview="preview"
			@file-selected="onFileSelected"
			@next="step = 2"
		/>

		<StepMapColumns
			v-if="step === 2"
			:instructions="$t('match-timetable-fields-to-csv-columns')"
			:field-defs="fieldDefs"
			:column-options="columnOptions"
			:column-map="columnMap"
			:validating="validating"
			:mapping-valid="mappingValid"
			:preview-rows="preview ? preview.rows : []"
			@validate="runValidation"
			@back="step = 1"
		/>

		<StepReviewIssues
			v-if="step === 3"
			:validation="validation"
			:error-count="errorCount"
			:warning-count="warningCount"
			:skip-invalid-rows.sync="skipInvalidRows"
			@next="step = 4"
			@back="step = 2"
		/>

		<StepSelectTimetable
			v-if="step === 4"
			:timetables="timetables"
			:loading-timetables="loadingTimetables"
			:importing="importing"
			:initial-name="newTimetableName"
			@import="runImport"
			@back="step = 3"
		/>

		<StepResults
			v-if="step === 5"
			:import-results="importResults"
			@reset="reset"
		/>
	</div>
</template>

<script>
import { CsvUploadWizardMixin } from '@/mixins';
import StepIndicator       from 'src/components/Util/StepIndicator.vue';
import StepPreview         from 'src/components/Util/Upload/StepPreview.vue';
import StepMapColumns      from 'src/components/Util/Upload/StepMapColumns.vue';
import StepReviewIssues    from 'src/components/Timetables/Upload/StepReviewIssues.vue';
import StepSelectTimetable from 'src/components/Timetables/Upload/StepSelectTimetable.vue';
import StepResults         from 'src/components/Timetables/Upload/StepResults.vue';

const FIELD_DEFS = [
	{ key: 'title_part1',  label: 'Event name (part 1)', required: false, hints: ['module', 'name', 'event', 'subject'] },
	{ key: 'title_part2',  label: 'Event name (part 2)', required: false, hints: ['session', 'subtitle'] },
	{ key: 'date',         label: 'Date',                required: true,  hints: ['date', 'day'] },
	{ key: 'time_from',    label: 'Start time',          required: true,  hints: ['start', 'from', 'begin', 'time from', 'timefrom'] },
	{ key: 'time_to',      label: 'End time',            required: true,  hints: ['end', 'to', 'finish', 'time to', 'timeto'] },
	{ key: 'teachers',     label: 'Teachers',            required: false, hints: ['teacher', 'tutor', 'lecturer', 'facilitator', 'staff'] },
	{ key: 'groups',       label: 'Groups',              required: false, hints: ['group', 'cohort'] },
	{ key: 'session_type', label: 'Session type',        required: true,  hints: ['session type', 'sessiontype', 'type', 'category'] },
	{ key: 'room',         label: 'Room / Location',     required: false, hints: ['room', 'location', 'venue', 'place'] },
	{ key: 'programme',    label: 'Programme code(s)',   required: false, hints: ['programme', 'program', 'course', 'prog'] },
	{ key: 'year',         label: 'Year group(s)',       required: false, hints: ['year', 'yr'] },
	{ key: 'notes',        label: 'Notes',               required: false, hints: ['note', 'notes', 'comment', 'remark'] },
];

export default {
	mixins: [CsvUploadWizardMixin],

	components: {
		StepIndicator,
		StepPreview,
		StepMapColumns,
		StepReviewIssues,
		StepSelectTimetable,
		StepResults,
	},

	data() {
		return {
			step:       1,
			stepLabels: ['Preview', 'Map Columns', 'Review Issues', 'Select Timetable'],

			// Step 2
			fieldDefs: FIELD_DEFS,
			columnMap: Object.fromEntries(FIELD_DEFS.map(f => [f.key, null])),

			// Step 4
			timetables:        [],
			loadingTimetables: false,
			newTimetableName:  '',
		};
	},

	computed: {
		csvEndpoints() {
			return {
				preview:  '/timetables/upload/preview',
				validate: '/timetables/upload/validate',
				import:   '/timetables/upload/import',
			};
		},
	},

	methods: {
		onFileSelected(file) {
			this.fetchPreview(file).then(res => {
				if (res && res.status === 'SUCCESS' && !this.newTimetableName) {
					this.newTimetableName = file.name.replace(/\.csv$/i, '');
				}
			});
		},

		runValidation() {
			this.postValidation().then(res => {
				if (res && res.status === 'SUCCESS') this.step = 3;
			});
		},

		loadTimetables() {
			this.loadingTimetables = true;
			this.$cfetch(`${this.$settings.APIBaseURL}/timetables/all`, { method: 'POST' })
				.then(res => {
					if (res.status === 'SUCCESS') {
						this.timetables = res.results || [];
						if (!this.timetables.length) this.timetableMode = 'new';
					}
				})
				.catch(err => console.error('Timetable list error:', err))
				.finally(() => { this.loadingTimetables = false; });
		},

		runImport({ mode, timetableId, name }) {
			this.postImport(
				mode === 'existing' ? { timetable_id: timetableId } : { timetable_name: name }
			).then(res => {
				if (res && res.status === 'SUCCESS') this.step = 5;
			});
		},
	},

	watch: {
		step(val) {
			if (val === 4 && !this.timetables.length) this.loadTimetables();
		},
	},
};
</script>
